import clsx from 'clsx';
import { CSSProperties, ReactNode, createElement } from 'react';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CSSTransition, SwitchTransition } from 'react-transition-group';

import { noopFunction } from '@vestico/utils';

import { getIsRTL } from '@vestico/i18n';

import { FormValidationContext, FormValidationProps } from '../CommonAppContext';
import { Padding, StepConfig, StepProps } from '../model';
import style from './NavigationContainer.module.css';

interface PageConfig {
  path: string;
  component: JSX.Element;
  bottomContent?: ReactNode | ReactNode[];
  fullWidth?: boolean;
  fullHeight?: boolean;
  padding?: Padding;
}

type StepPath = string;
type FormValidationStepPathValidMap = Map<StepPath, boolean>;

export interface NavigationContainerProps {
  steps: StepConfig[];
  currentStepIndex: number;
  validateStep: boolean;
  currentStepIsValid: (valid: boolean) => void;
  onAnimate: (animating: boolean) => void;
  onSubmit?: () => void;
  onSubmitStart?: () => void;
  onSubmitEnd?: () => void;
  navigationButtons: ReactNode;
  footer?: ReactNode;
}

export const NavigationContainer = ({
  steps: stepConfigs,
  validateStep,
  currentStepIndex,
  navigationButtons,
  currentStepIsValid,
  onSubmit,
  onSubmitStart,
  onSubmitEnd,
  footer,
  onAnimate,
}: NavigationContainerProps) => {
  const submittingStep = useMemo(() => validateStep, [validateStep]);
  const pages: PageConfig[] = stepConfigs.map((step: StepConfig) => {
    const { class: stepClass, path, title, bottomContent, fullWidth, fullHeight, padding, showTitleInStep } = step;
    const showTitle = showTitleInStep === undefined || showTitleInStep === null || !!showTitleInStep;
    const props: StepProps = {
      submitting: submittingStep,
      path: path,
      title: showTitle ? title : '',
    };
    const pageConfig: PageConfig = {
      path: path,
      bottomContent,
      component: createElement(stepClass, props),
      fullWidth,
      fullHeight,
      padding,
    };
    return pageConfig;
  });

  const location = useLocation();
  const navigate = useNavigate();
  const isRTL = getIsRTL();

  // Initialize with empty string. The page direction memo will set it accordingly
  const currentPath = location.pathname;

  const previousPageDirection = useRef('none');
  const [previousIndex, setPreviousIndex] = useState(0);
  const [animating, setAnimating] = useState(false);

  useLayoutEffect(() => {
    if (previousIndex !== undefined && previousIndex !== currentStepIndex) {
      setAnimating(true);

      if (previousIndex < currentStepIndex) {
        const newPage = pages[currentStepIndex];
        navigate(newPage.path);
      } else {
        navigate(-1);
      }
    }
    setPreviousIndex(currentStepIndex);
  }, [currentStepIndex]);

  const pageDirection = useMemo(() => {
    if (previousIndex === currentStepIndex) {
      return previousPageDirection.current;
    }
    if (previousIndex !== undefined && previousIndex !== currentStepIndex) {
      const goBack = previousIndex <= currentStepIndex;
      return (goBack && !isRTL) || (!goBack && isRTL) ? 'left' : 'right';
    }
    return isRTL ? 'right' : 'left';
  }, [currentStepIndex, isRTL, previousIndex]);

  useEffect(() => {
    previousPageDirection.current = pageDirection;
  }, [pageDirection]);

  const animationContainerRef = useRef<HTMLDivElement>(null);

  const pageConfigAtCurrentIndex = useCallback(() => {
    return currentStepIndex !== undefined ? pages[currentStepIndex] : undefined;
  }, [currentStepIndex, pages]);

  const pageConfigForPath = useCallback(
    (pathname: string) => {
      for (const pageConfig of pages) {
        if (pageConfig.path === pathname) {
          return pageConfig;
        }
      }

      // Fallback to use current step index if page config not available
      // This is needed when running in FAP and we have two navigations running concurrently
      return pageConfigAtCurrentIndex();
    },
    [pages, pageConfigAtCurrentIndex]
  );

  useEffect(() => {
    onAnimate(animating);
  }, [animating, onAnimate]);

  // PROCEED TO NEXT STEP IF POSSIBLE

  // Purposefully never change these if pages change
  const steps: FormValidationStepPathValidMap = useMemo(() => {
    const map: FormValidationStepPathValidMap = new Map();
    for (const page of pages) {
      map.set(page.path, false);
    }
    return map;
  }, []);

  const formStepAtPathIsValid = useCallback((path: string) => !!steps.get(path), [steps]);

  useEffect(() => {
    for (const page of pages) {
      const { path } = page;
      if (steps.get(path) === undefined) {
        steps.set(path, false);
      }
    }
  }, [pages]);

  const moveToNextStepIfPossible = useCallback(() => {
    const currentStepValid = formStepAtPathIsValid(currentPath);
    currentStepIsValid(currentStepValid);
  }, [formStepAtPathIsValid, currentPath, currentStepIsValid]);

  useEffect(() => {
    if (!submittingStep) {
      return;
    }

    moveToNextStepIfPossible();
  }, [submittingStep]);

  // STEPS HANDLING

  const updateStep = useCallback((step: string, valid: boolean) => steps.set(step, valid), [steps]);

  const pageConfig = useMemo(() => pageConfigForPath(currentPath), [pageConfigForPath, currentPath]);
  const bottomContent = useMemo(() => (pageConfig ? pageConfig.bottomContent : null), [pageConfig]);

  const resetAnimating = useCallback(() => {
    const newPageConfig = pageConfigAtCurrentIndex();
    if (newPageConfig?.path === currentPath) {
      setAnimating(false);
    }
  }, [pageConfigAtCurrentIndex, currentPath]);

  // We use a ref to not trigger re-renders when the form validation context changes
  const formValidationContextRef = useRef<FormValidationProps>({
    updateStep,
    submit: noopFunction,
    submitStart: noopFunction,
    submitEnd: noopFunction,
  });
  useLayoutEffect(() => {
    formValidationContextRef.current.updateStep = updateStep;
    formValidationContextRef.current.submit = onSubmit ?? noopFunction;
    formValidationContextRef.current.submitStart = onSubmitStart ?? noopFunction;
    formValidationContextRef.current.submitEnd = onSubmitEnd ?? noopFunction;
  }, [onSubmit, updateStep, onSubmitStart, onSubmitEnd]);

  const contentStyle: CSSProperties = useMemo(() => {
    if (!pageConfig) {
      return {};
    }
    const { padding } = pageConfig;
    if (!padding) {
      return {};
    }
    const newProps: CSSProperties = {};
    const { top, left, right, bottom } = padding;
    if (top !== undefined) {
      newProps.marginTop = top;
    }
    if (bottom !== undefined) {
      newProps.marginBottom = bottom;
    }
    if (left !== undefined) {
      newProps.marginLeft = left;
    }
    if (right !== undefined) {
      newProps.marginRight = right;
    }
    return newProps;
  }, [pageConfig]);

  return (
    <FormValidationContext.Provider value={formValidationContextRef.current}>
      <div className={clsx(style.root)}>
        <div
          className={clsx(style.content, { [style.fullWidth]: !!pageConfig?.fullWidth }, { [style.fullHeight]: !!pageConfig?.fullHeight })}
          style={contentStyle}
        >
          <>
            <SwitchTransition mode="out-in">
              <CSSTransition
                key={pageConfig?.path ?? '/'}
                timeout={400}
                classNames={pageDirection}
                nodeRef={animationContainerRef}
                addEndListener={resetAnimating}
              >
                <div
                  className={`route__container ${currentStepIndex !== undefined && !!pageConfig?.fullHeight ? 'fullHeight' : ''} `}
                  ref={animationContainerRef}
                >
                  {pageConfig?.component}
                  {bottomContent}
                </div>
              </CSSTransition>
            </SwitchTransition>
            {navigationButtons}
          </>
          {footer}
        </div>
      </div>
    </FormValidationContext.Provider>
  );
};
